import * as React from 'react'

import { MobXProviderContext } from 'mobx-react'

import { ITypeFieldFilterOptions } from '~/client/graph'
import { IResourceProperty } from '~/client/src/shared/components/ResourceCardList/types'
import type { ICreationFieldOptions } from '~/client/src/shared/components/ResourceViewEdit/useResourceFields'
import { IWarningNote } from '~/client/src/shared/components/WarningNote/WarningNote'
import {
  getAllowedTemplateIds,
  hasTemplateAllowListFilter,
  isTemplateFilter,
} from '~/client/src/shared/constants/FilterPropertyMaps/ResourceFilterPropMap'
import SortOrder from '~/client/src/shared/enums/SortOrder'
import {
  getPersistableResourceFilterSelectionIds,
  IQuickFilters,
  retainQuickFilterSelectionIds,
} from '~/client/src/shared/hooks/useResourceQuickFilters'
import PermitField from '~/client/src/shared/models/PermitField'
import UserProject from '~/client/src/shared/models/UserProject'
import { buildResourceUiConfig } from '~/client/src/shared/resources/builders/buildResourceUiConfig'
import { IResourceRecord } from '~/client/src/shared/resources/definitions/types'
import type { ResourceSchema } from '~/client/src/shared/resources/domain/resourceSchema'
import { useResolveGroupPresentation } from '~/client/src/shared/resources/presentation/resolveGroupPresentation'
import {
  evaluateFilters,
  pruneMissingIdAllowances,
} from '~/client/src/shared/utils/filterEvaluationHelpers'

import { buildPickerGroupConfig } from './buildPickerGroupConfig'
import PickerCreateButton from './compound/PickerCreateButton'
import PickerCreationPanel from './compound/PickerCreationPanel'
import PickerEmptyState from './compound/PickerEmptyState'
import PickerFooter from './compound/PickerFooter'
import PickerItem from './compound/PickerItem'
import PickerList from './compound/PickerList'
import PickerToolbar from './compound/PickerToolbar'
import { toCompoundIds } from './filterCompoundIds'
import {
  areLocalDefaultsEqual,
  ILocalDefaults,
  IParsedDefaults,
} from './parseDefaultOptions'
import {
  ResourceFormData,
  ResourcePickerContext,
} from './ResourcePickerContext'
import { useCleanStaleDefaults } from './useCleanStaleDefaults'
import { useResourceFieldOptions } from './useResourceFieldOptions'
import { useResourcePickerState } from './useResourcePickerState'
import { renderResourceTypeIcon } from './useResourceRenderItem'
import {
  IResourcePickerTypeSource,
  useResourceTypeSources,
} from './useResourceTypeSources'

interface ICommonProps {
  mode: 'single' | 'multi'
  selectedIds?: string[]
  onSelect: (ids: string[]) => void
  allowSelectExisting?: boolean
  loading: boolean
  propertiesReady: boolean
  recordPredicate?: (record: IResourceRecord) => boolean
  projectId?: string
  onClose?: () => void
  isItemDisabled?: (recordId: string) => boolean
  getNote?: (recordId: string) => IWarningNote | null
  /** Single mode, in the LBS tree: a row press can mean expand, so a pick waits for Apply. */
  stageTreeSelection?: boolean
  allowEmptySelection?: boolean
  children: React.ReactNode
}

interface ISingleTypeProps {
  /**
   * Every record, narrowed by `recordPredicate` and `filterOptions` rather than up front:
   * the LBS tree places an offered record under ancestors that are not offered themselves.
   */
  records: IResourceRecord[]
  resourceTypeId: string
  properties?: IResourceProperty[]
  resourceSchema?: ResourceSchema
  onCreateResource?: (formData: ResourceFormData) => Promise<string | null>
  initialCreating?: boolean
  defaults?: IParsedDefaults
  getFilterValues?: (key: string) => string[]
  filterOptions?: ITypeFieldFilterOptions[]
  workflowFieldsMap?: Record<string, PermitField>
  onCreatingChange?: (creating: boolean) => void
  onDefaultsChange?: (snapshot: ILocalDefaults) => void
  startCreatingRef?: React.MutableRefObject<(() => void) | null>
  creationRootDefaults?: Record<string, any>
  creationFieldOptions?: ICreationFieldOptions
  resourceTypes?: never
}

// Several resource types in one list, grouped Resource first. The first is primary: its
// groupings and sorts are offered. Creation, saved defaults and field filters are
// single-type only.
type IMultiTypeProps = {
  /** Keep stable. */
  resourceTypes: readonly [
    IResourcePickerTypeSource,
    ...IResourcePickerTypeSource[],
  ]
} & { [K in Exclude<keyof ISingleTypeProps, 'resourceTypes'>]?: never }

type IProps = ICommonProps & (ISingleTypeProps | IMultiTypeProps)

const NO_FILTER_CATEGORIES: ReturnType<
  typeof useResourceFieldOptions
>['filterCategories'] = []

const ResourcePickerRoot: React.FC<IProps> = ({
  records: singleRecords,
  resourceTypeId: singleTypeId,
  resourceTypes,
  mode,
  selectedIds,
  onSelect,
  onCreateResource,
  initialCreating,
  allowSelectExisting: allowSelectExistingProp,
  loading,
  defaults,
  getFilterValues,
  properties: singleProperties,
  propertiesReady,
  resourceSchema: singleSchema,
  filterOptions,
  workflowFieldsMap,
  projectId,
  recordPredicate,
  onClose,
  onCreatingChange,
  onDefaultsChange,
  startCreatingRef,
  isItemDisabled,
  creationRootDefaults,
  creationFieldOptions,
  getNote,
  stageTreeSelection,
  allowEmptySelection,
  children,
}) => {
  const stores = React.useContext(MobXProviderContext) as any
  const currentUser = stores.state?.user
  const currentUserCompanyId = UserProject.getCompanyId(
    currentUser,
    stores.userProjectsStore,
  )

  const sources = React.useMemo<readonly IResourcePickerTypeSource[]>(
    () =>
      resourceTypes ?? [
        {
          resourceTypeId: singleTypeId,
          records: singleRecords,
          properties: singleProperties,
          resourceSchema: singleSchema,
        },
      ],
    [
      resourceTypes,
      singleTypeId,
      singleRecords,
      singleProperties,
      singleSchema,
    ],
  )
  const {
    isMixed,
    records,
    fieldProjector,
    searchableKeys,
    quickFilterDefinitions,
    sortKeys,
    getRecordTypeId,
    getFieldHierarchy,
    getPropertyConfig,
  } = useResourceTypeSources(sources, {
    currentUserId: currentUser?.id,
    currentUserCompanyId,
  })
  const { resourceTypeId, properties, resourceSchema } = sources[0]

  const uiConfig = React.useMemo(
    () => buildResourceUiConfig(resourceTypeId),
    [resourceTypeId],
  )

  const customFilterMatchers = React.useMemo(
    () =>
      Object.fromEntries(
        quickFilterDefinitions.map(definition => [
          definition.key,
          (record: IResourceRecord, selectedValues: Set<string>) =>
            selectedValues.has(definition.optionId) &&
            definition.matchesRecord(record),
        ]),
      ),
    [quickFilterDefinitions],
  )

  const baseRecords = React.useMemo(() => {
    let filtered = records

    // `records` excludes soft-deleted resources, so it is also the authority on
    // which configured ids still exist. Stale ones are pruned rather than
    // matched, or a field restricted to since-deleted resources shows nothing.
    const recordFilterOptions = pruneMissingIdAllowances(
      filterOptions?.filter(f => !isTemplateFilter(f)),
      new Set(records.map(record => record.id)),
    )

    if (recordFilterOptions.length) {
      filtered = filtered.filter(record =>
        evaluateFilters(recordFilterOptions, record, workflowFieldsMap),
      )
    }

    if (recordPredicate) {
      filtered = filtered.filter(recordPredicate)
    }

    return filtered
  }, [records, filterOptions, workflowFieldsMap, recordPredicate])
  const pickableIds = React.useMemo(
    () => new Set(baseRecords.map(record => record.id)),
    [baseRecords],
  )

  const templateAllowList = React.useMemo<string[] | null>(() => {
    if (!hasTemplateAllowListFilter(filterOptions)) return null
    return getAllowedTemplateIds(filterOptions)
  }, [filterOptions])

  const pickerState = useResourcePickerState({
    mode,
    resourceTypeId,
    uiConfig,
    records: baseRecords,
    searchableKeys,
    fieldProjector,
    initialSelectedIds: selectedIds,
    initialSortKey: defaults?.sortKey,
    initialSortOrder: defaults?.sortOrder,
    initialGroupByKey: defaults?.groupByKey,
    initialFilterSelection: defaults?.filterSelection,
    customFilterMatchers,
    allowEmptySelection,
  })

  const commitsOnPick =
    mode === 'single' && !(stageTreeSelection && pickerState.isTreeGrouping)
  const { toggleItem, selectedIds: currentIds } = pickerState
  const pickItem = React.useCallback(
    (id: string) => {
      toggleItem(id)
      if (commitsOnPick) {
        onSelect(allowEmptySelection && currentIds.has(id) ? [] : [id])
      }
    },
    [toggleItem, commitsOnPick, onSelect, allowEmptySelection, currentIds],
  )

  const incomingDefaults = React.useMemo<ILocalDefaults>(
    () => ({
      sortKey: pickerState.initialSortKey,
      sortOrder: pickerState.initialSortOrder,
      groupByKey: pickerState.initialGroupByKey,
      filterSelection: defaults?.filterSelection
        ? toCompoundIds(defaults.filterSelection)
        : [],
    }),
    [
      defaults?.filterSelection,
      pickerState.initialSortKey,
      pickerState.initialSortOrder,
      pickerState.initialGroupByKey,
    ],
  )
  const previousIncomingDefaults = React.useRef(incomingDefaults)
  const incomingDefaultsChanged = !areLocalDefaultsEqual(
    previousIncomingDefaults.current,
    incomingDefaults,
  )

  const applyDefaultsSnapshot = React.useCallback(
    (next: ILocalDefaults) => {
      pickerState.setDefaults({
        ...next,
        filterSelection: retainQuickFilterSelectionIds(
          next.filterSelection,
          pickerState.filterSelectionAsIds,
        ),
      })
    },
    [pickerState.setDefaults, pickerState.filterSelectionAsIds],
  )

  React.useLayoutEffect(() => {
    if (!incomingDefaultsChanged) return
    previousIncomingDefaults.current = incomingDefaults
    applyDefaultsSnapshot(incomingDefaults)
  }, [incomingDefaultsChanged, incomingDefaults, applyDefaultsSnapshot])

  const wrappedStartCreating = React.useCallback(() => {
    pickerState.startCreating()
    onCreatingChange?.(true)
  }, [pickerState.startCreating, onCreatingChange])

  const wrappedStopCreating = React.useCallback(() => {
    pickerState.stopCreating()
    onCreatingChange?.(false)
  }, [pickerState.stopCreating, onCreatingChange])

  React.useEffect(() => {
    if (startCreatingRef) {
      startCreatingRef.current = wrappedStartCreating
    }
  }, [startCreatingRef, wrappedStartCreating])

  const fieldOptions = useResourceFieldOptions({
    resourceTypeId,
    properties,
    getFilterValues,
    records: isMixed ? undefined : baseRecords,
  })
  const { groupByOptions } = fieldOptions
  const sortOptions = React.useMemo(
    () =>
      sortKeys
        ? fieldOptions.sortOptions.filter(option => sortKeys.has(option.key))
        : fieldOptions.sortOptions,
    [fieldOptions.sortOptions, sortKeys],
  )
  const filterCategories = isMixed
    ? NO_FILTER_CATEGORIES
    : fieldOptions.filterCategories
  const extraValidFilterKeys = React.useMemo(
    () => new Set(Object.keys(customFilterMatchers)),
    [customFilterMatchers],
  )
  const skipGroupByKeys = React.useMemo(
    () => new Set([uiConfig.noneGroupingKey]),
    [uiConfig.noneGroupingKey],
  )

  const localPickerDefaults = React.useMemo<ILocalDefaults>(
    () => ({
      sortKey: pickerState.sortKey,
      sortOrder: pickerState.sortOrder,
      groupByKey: pickerState.groupByKey,
      filterSelection: pickerState.filterSelectionAsIds,
    }),
    [
      pickerState.sortKey,
      pickerState.sortOrder,
      pickerState.groupByKey,
      pickerState.filterSelectionAsIds,
    ],
  )
  const pickerDefaults = incomingDefaultsChanged
    ? incomingDefaults
    : localPickerDefaults

  // Where clearing the grouping, or a grouping no longer offered, lands: the picker
  // default while it is offered, else the first grouping that is.
  const fallbackGroupByKey = pickerState.allowNoneGrouping
    ? null
    : ((
        groupByOptions.find(
          ({ key }) => key === uiConfig.pickerDefaultGroupingKey,
        ) ?? groupByOptions[0]
      )?.key ?? null)

  useCleanStaleDefaults({
    propertiesReady,
    sortOptions,
    groupByOptions,
    filterCategories,
    defaults: pickerDefaults,
    onChange: applyDefaultsSnapshot,
    skipGroupByKeys,
    extraValidFilterKeys,
    fallbackGroupByKey,
  })

  // Auto-start creation mode when only create-new is allowed
  React.useEffect(() => {
    if (initialCreating && onCreateResource && !pickerState.isCreating) {
      wrappedStartCreating()
    }
  }, [initialCreating, onCreateResource])

  // Notify parent synchronously (not via useEffect) so the snapshot includes the new value
  const persistableFilterSelectionAsIds = React.useMemo(
    () =>
      getPersistableResourceFilterSelectionIds(
        pickerState.filterSelectionAsIds,
      ),
    [pickerState.filterSelectionAsIds],
  )

  const notifyDefaults = React.useCallback(
    (overrides: Partial<ILocalDefaults>) => {
      const filterSelection =
        overrides.filterSelection !== undefined
          ? getPersistableResourceFilterSelectionIds(overrides.filterSelection)
          : persistableFilterSelectionAsIds

      onDefaultsChange?.({
        sortKey: pickerState.sortKey,
        sortOrder: pickerState.sortOrder,
        groupByKey: pickerState.groupByKey,
        ...overrides,
        filterSelection,
      })
    },
    [
      onDefaultsChange,
      pickerState.sortKey,
      pickerState.sortOrder,
      pickerState.groupByKey,
      persistableFilterSelectionAsIds,
    ],
  )

  const wrappedSetGroupByKey = React.useCallback(
    (key: string | null) => {
      pickerState.setGroupByKey(key)
      notifyDefaults({ groupByKey: key })
    },
    [pickerState.setGroupByKey, notifyDefaults],
  )

  const wrappedSetFilterSelection = React.useCallback(
    (ids: string[]) => {
      pickerState.setFilterSelection(ids)
      notifyDefaults({ filterSelection: ids })
    },
    [pickerState.setFilterSelection, notifyDefaults],
  )

  const setSort = React.useCallback(
    (key: string | null, order?: SortOrder) => {
      pickerState.setSortKey(key)
      if (order !== undefined) pickerState.setSortOrder(order)
      notifyDefaults({
        sortKey: key,
        ...(order !== undefined && { sortOrder: order }),
      })
    },
    [pickerState.setSortKey, pickerState.setSortOrder, notifyDefaults],
  )

  const quickFilters: IQuickFilters = React.useMemo(() => {
    const meDefinition = quickFilterDefinitions[0]
    const myCompanyDefinition = quickFilterDefinitions[1]

    const toggleQuickFilter = (key: string, optionId: string) => {
      const nextSelection = new Map(pickerState.filterSelection)
      const currentValues = nextSelection.get(key)

      if (currentValues?.has(optionId)) {
        nextSelection.delete(key)
      } else {
        nextSelection.set(key, new Set([optionId]))
      }

      wrappedSetFilterSelection(toCompoundIds(nextSelection))
    }

    return {
      isOnlyMe: !!pickerState.filterSelection
        .get(meDefinition.key)
        ?.has(meDefinition.optionId),
      isOnlyMyCompany: !!pickerState.filterSelection
        .get(myCompanyDefinition.key)
        ?.has(myCompanyDefinition.optionId),
      toggleOnlyMe: () =>
        toggleQuickFilter(meDefinition.key, meDefinition.optionId),
      toggleOnlyMyCompany: () =>
        toggleQuickFilter(
          myCompanyDefinition.key,
          myCompanyDefinition.optionId,
        ),
      hasUserProperties: meDefinition.isAvailable,
      hasCompanyProperties: myCompanyDefinition.isAvailable,
      resetQuickFilters: () => {
        const nextSelection = new Map(pickerState.filterSelection)
        nextSelection.delete(meDefinition.key)
        nextSelection.delete(myCompanyDefinition.key)
        wrappedSetFilterSelection(toCompoundIds(nextSelection))
      },
    }
  }, [
    pickerState.filterSelection,
    wrappedSetFilterSelection,
    quickFilterDefinitions,
  ])

  const { groupByKey, isTreeGrouping } = pickerState

  const typeIcons = React.useMemo(
    () =>
      new Map(
        sources.map(source => [
          source.resourceTypeId,
          source.icon ?? renderResourceTypeIcon(source.resourceTypeId),
        ]),
      ),
    [sources],
  )
  const getTypeIcon = React.useCallback(
    (typeId: string) => typeIcons.get(typeId),
    [typeIcons],
  )

  const getSourceRenderItem = React.useMemo(() => {
    if (!sources.some(source => source.renderItem)) return undefined
    const renderers = new Map(
      sources.map(source => [source.resourceTypeId, source.renderItem]),
    )
    return (record: IResourceRecord) => renderers.get(getRecordTypeId(record))
  }, [sources, getRecordTypeId])

  const grouping = React.useMemo(
    () =>
      groupByKey && groupByKey !== uiConfig.noneGroupingKey && !isTreeGrouping
        ? buildPickerGroupConfig({
            groupByKey,
            records: pickerState.sortedRecords,
            getResolvedField: pickerState.getResolvedField,
            getRecordTypeId,
            bands: sources.map(source => ({
              resourceTypeId: source.resourceTypeId,
              label: source.label ?? source.resourceTypeId,
              hierarchy: getFieldHierarchy(source.resourceTypeId, groupByKey),
            })),
          })
        : null,
    [
      groupByKey,
      uiConfig.noneGroupingKey,
      isTreeGrouping,
      pickerState.sortedRecords,
      pickerState.getResolvedField,
      getRecordTypeId,
      sources,
      getFieldHierarchy,
    ],
  )

  const resolveGroupPresentation = useResolveGroupPresentation()

  const getGroupPresentation = React.useCallback(
    (groupKey: string, count?: number) => {
      const group = grouping?.describeGroup(groupKey) ?? {
        resourceTypeId,
        value: groupKey,
      }
      if (group.value === null) {
        return { label: grouping.config.getGroupLabel(groupKey, count ?? 0) }
      }
      return resolveGroupPresentation({
        propertyConfig: groupByKey
          ? getPropertyConfig(group.resourceTypeId, groupByKey)
          : undefined,
        groupKey: group.value,
        count,
        propertyName: groupByKey,
        resourceTypeId: group.resourceTypeId,
      })
    },
    [
      grouping,
      resourceTypeId,
      groupByKey,
      getPropertyConfig,
      resolveGroupPresentation,
    ],
  )

  const ctx = React.useMemo(() => {
    // The raw sort setters skip the defaults notification; setSort is the one entry.
    const {
      setSortKey: _setSortKey,
      setSortOrder: _setSortOrder,
      ...pickerStateWithoutSortSetters
    } = pickerState
    return {
      ...pickerStateWithoutSortSetters,
      fallbackGroupByKey,
      resourceTypeId,
      setGroupByKey: wrappedSetGroupByKey,
      setFilterSelection: wrappedSetFilterSelection,
      setSort,
      startCreating: wrappedStartCreating,
      stopCreating: wrappedStopCreating,
      projectId,
      loading,
      sortOptions,
      groupByOptions,
      filterCategories,
      onSelect,
      onCreateResource,
      allowSelectExisting: allowSelectExistingProp ?? !initialCreating,
      onClose,
      quickFilters,
      templateAllowList,
      resourceSchema,
      getGroupPresentation,
      grouping,
      unfilteredRecords: records,
      pickableIds,
      isItemDisabled,
      creationRootDefaults,
      creationFieldOptions,
      getNote,
      pickItem,
      commitsOnPick,
      allowEmptySelection,
      getRecordTypeId,
      getTypeIcon,
      getSourceRenderItem,
    }
  }, [
    pickerState,
    fallbackGroupByKey,
    resourceTypeId,
    wrappedSetGroupByKey,
    wrappedSetFilterSelection,
    setSort,
    wrappedStartCreating,
    wrappedStopCreating,
    projectId,
    loading,
    sortOptions,
    groupByOptions,
    filterCategories,
    onSelect,
    onCreateResource,
    allowSelectExistingProp,
    initialCreating,
    onClose,
    quickFilters,
    templateAllowList,
    resourceSchema,
    getGroupPresentation,
    grouping,
    records,
    pickableIds,
    isItemDisabled,
    creationRootDefaults,
    creationFieldOptions,
    getNote,
    pickItem,
    commitsOnPick,
    allowEmptySelection,
    getRecordTypeId,
    getTypeIcon,
    getSourceRenderItem,
  ])

  return (
    <ResourcePickerContext.Provider value={ctx}>
      <div className="tw:flex tw:flex-col tw:h-full">
        {pickerState.isCreating ? <PickerCreationPanel /> : children}
      </div>
    </ResourcePickerContext.Provider>
  )
}

const ResourcePicker = Object.assign(ResourcePickerRoot, {
  Toolbar: PickerToolbar,
  List: PickerList,
  Item: PickerItem,
  EmptyState: PickerEmptyState,
  Footer: PickerFooter,
  CreateButton: PickerCreateButton,
})

export default ResourcePicker
